import {Edge, Node} from 'reactflow';

import {assert} from 'Asserts';
import {
  ProcedureEdgeNode,
  ProcedureEdgeType,
  ProcedureType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ExperimentMethod} from 'studies/typed/shared/ExperimentalInputs';
import {
  BaseCondition,
  ConditionGroup,
  isPersistedCondition,
} from 'studies/typed/shared/useExperimentDesignState';
import {hideFlowchartElementBeforeLayout} from 'ui-library/reactflow/layout/hideFlowchartElementBeforeLayoutUtils';

import type {MethodIdAndName} from './activeMethodUtils';
import {
  createProcedureRunDesignFlowchartColumnNode,
  FALLBACK_COLUMN_NODE_HEIGHT,
  ProcedureRunDesignFlowchartColumnNodeData,
} from './ProcedureRunDesignFlowchartColumnNode';
import {getNodeConnectionRoles} from './procedureRunDesignFlowchartConnectionUtils';
import {
  makeProcedureRunDesignFlowchartEdge,
  ProcedureRunDesignFlowchartEdgeData,
  ProcedureRunDesignFlowchartEdgeVariant,
} from './ProcedureRunDesignFlowchartEdge';
import {
  createProcedureRunDesignFlowchartNode,
  FALLBACK_NODE_WIDTH,
  FALLBACK_NODE_HEIGHT,
  getProcedureRunDesignFlowchartNodeId,
  ProcedureRunDesignFlowchartNodeData,
} from './ProcedureRunDesignFlowchartNode';
import {UnitOperationStatusType} from './useMultiProcedureRunStudyProgress';

/** Maps a flowchart node id to its method task status. Write and read paths must use matching keys. */
export type StatusByNodeId = ReadonlyMap<string, UnitOperationStatusType>;

/** A directed link between two grid nodes. Both planning and execution edges reduce to this primitive. */
export interface FlowchartEdgeSpec {
  sourceNodeId: string;
  targetNodeId: string;
  /** Defaults to 'default'. */
  variant?: ProcedureRunDesignFlowchartEdgeVariant;
}

export interface GridLayoutOptions {
  horizontalGap: number;
  verticalGap: number;
  startX: number;
  startY: number;
}

/**
 * Creates the initial set of hidden nodes from domain data. This is the single place that maps
 * domain objects (conditions, methods) to ReactFlow nodes. All nodes start with opacity 0% so
 * ReactFlow can measure them before the first visible frame.
 */
export function makeInitialNodes(
  conditions: Array<BaseCondition>,
  methods: Array<ExperimentMethod>,
  // When false persisted conditions are read-only and render lock icon
  allowEditingPersistedConditions: boolean,
  procedureType: ProcedureType,
  activeMethodId: string | null = null,
  statusByNodeId?: StatusByNodeId,
  // Seeds the edge-dependent showPersistentSourceHandle so the first paint is flicker-free. After that the
  // value is kept live off the rendered edges.
  edgeSpecs: Array<FlowchartEdgeSpec> = []
): {
  columnNodes: Array<Node<ProcedureRunDesignFlowchartColumnNodeData>>;
  rows: Array<Array<Node<ProcedureRunDesignFlowchartNodeData>>>;
} {
  const activeMethodIndex =
    activeMethodId == null ? -1 : methods.findIndex((method) => method.id === activeMethodId);
  const columnNodes = methods.map((method, methodIndex) =>
    hideFlowchartElementBeforeLayout(
      createProcedureRunDesignFlowchartColumnNode({
        methodId: method.id,
        methodName: method.name,
        factorsSet: 0, // TODO(BNCH-153703): Add factors set
        procedureType,
        isActiveMethod: method.id === activeMethodId,
        isHighlighted: false,
        isUpstreamOfActiveMethod: activeMethodIndex > 0 && methodIndex < activeMethodIndex,
      })
    )
  );

  const {roleByNodeId, showPersistentSourceHandleByNodeId} = getNodeConnectionRoles(
    conditions,
    methods,
    edgeSpecs.map((spec) => ({source: spec.sourceNodeId, target: spec.targetNodeId})),
    activeMethodId
  );

  const rows: Array<Array<Node<ProcedureRunDesignFlowchartNodeData>>> = [];
  conditions.forEach((condition, conditionIndex) => {
    const isLocked = !allowEditingPersistedConditions && isPersistedCondition(condition);
    for (let replicateIndex = 0; replicateIndex < condition.numReplicates; replicateIndex++) {
      rows.push(
        methods.map((method) => {
          const nodeId = getProcedureRunDesignFlowchartNodeId({
            conditionIndex,
            replicateIndex,
            methodId: method.id,
          });
          return hideFlowchartElementBeforeLayout(
            createProcedureRunDesignFlowchartNode({
              conditionIndex,
              replicateIndex,
              methodId: method.id,
              conditionName: condition.name,
              replicateNumber: replicateIndex + 1,
              conditionDescription: condition.description,
              isHovered: false,
              isHighlighted: false,
              isLocked,
              status: statusByNodeId?.get(nodeId),
              connectionRole: roleByNodeId.get(nodeId) ?? null,
              showPersistentSourceHandle: showPersistentSourceHandleByNodeId.get(nodeId) ?? false,
            })
          );
        })
      );
    }
  });

  return {columnNodes, rows};
}

/**
 * Maps {@link FlowchartEdgeSpec}s to hidden ReactFlow edges wired to each endpoint's deterministic
 * handles. Edges start hidden so they reveal atomically with their nodes once layout settles.
 */
export function makeInitialEdges(
  edgeSpecs: Array<FlowchartEdgeSpec>
): Array<Edge<ProcedureRunDesignFlowchartEdgeData>> {
  return edgeSpecs.map((spec) => hideFlowchartElementBeforeLayout(makeProcedureRunDesignFlowchartEdge(spec)));
}

/**
 * A directed link between two template methods, identified by their method definition version ids.
 * The planning-time intermediate between the template method graph (`ProcedureEdgeNode`s) and the
 * per-replicate {@link FlowchartEdgeSpec}s.
 */
export interface MethodLink {
  sourceMethodId: string;
  targetMethodId: string;
}

/**
 * Adapts a template flowchart's edges into {@link MethodLink}s, keeping only `METHOD_EDGE`s (the
 * method-to-method execution order) and dropping `MATERIAL_EDGE`s (material flow).
 */
export function methodLinksFromProcedureEdges(edges: Array<ProcedureEdgeNode>): Array<MethodLink> {
  return edges
    .filter((edge) => edge.type === ProcedureEdgeType.METHOD_EDGE)
    .map((edge) => ({sourceMethodId: edge.sourceId, targetMethodId: edge.targetId}));
}

interface EdgeSpecsFromMethodLinksArgs {
  conditions: Array<BaseCondition>;
  methods: Array<ExperimentMethod>;
  links: Array<MethodLink>;
  shouldIncludeCondition?: (condition: BaseCondition, conditionIndex: number) => boolean;
  getVariantForLink?: (link: MethodLink) => ProcedureRunDesignFlowchartEdgeVariant;
}

/**
 * Fans out method-level {@link MethodLink}s into per-replicate {@link FlowchartEdgeSpec}s: each link is
 * replicated within every (condition, replicate) row, connecting the two methods' grid nodes on that
 * row. Links whose endpoints aren't among the rendered `methods` are skipped so no edge points at a
 * nonexistent node.
 *
 * `shouldIncludeCondition` optionally filters which condition rows receive edges. This is necessary for
 * when adding conditions to a procedure run, and we want to show edges for unpersisted conditions based on
 * the template procedure's method edges.
 */
export function edgeSpecsFromMethodLinks({
  conditions,
  methods,
  links,
  shouldIncludeCondition,
  getVariantForLink,
}: EdgeSpecsFromMethodLinksArgs): Array<FlowchartEdgeSpec> {
  const validMethodIds = new Set(methods.map((method) => method.id));
  const validLinks = links.filter(
    (link) => validMethodIds.has(link.sourceMethodId) && validMethodIds.has(link.targetMethodId)
  );

  const edgeSpecs: Array<FlowchartEdgeSpec> = [];
  conditions.forEach((condition, conditionIndex) => {
    if (shouldIncludeCondition != null && !shouldIncludeCondition(condition, conditionIndex)) {
      return;
    }
    for (let replicateIndex = 0; replicateIndex < condition.numReplicates; replicateIndex++) {
      validLinks.forEach((link) => {
        const variant = getVariantForLink?.(link);
        edgeSpecs.push({
          sourceNodeId: getProcedureRunDesignFlowchartNodeId({
            conditionIndex,
            replicateIndex,
            methodId: link.sourceMethodId,
          }),
          targetNodeId: getProcedureRunDesignFlowchartNodeId({
            conditionIndex,
            replicateIndex,
            methodId: link.targetMethodId,
          }),
          ...(variant != null ? {variant} : {}),
        });
      });
    }
  });
  return edgeSpecs;
}

/** What's needed to place a PMEI in the design grid: its template method (column) and condition-replicate (row). */
export interface MethodExecutionInstanceInfoForPlacement {
  pmeiId: string;
  methodId: string;
  conditionId: string;
  replicateNumber: number;
}

/**
 * A directed edge in a `ProcedureRun`'s runtime condition graph, from a source PMEI to a target PMEI.
 */
export interface ConditionReplicateEdge {
  sourcePmeiId: string;
  targetPmeiId: string;
}

/** A PMEI resolved to the grid node that renders it, plus its template method ID (for downstream styling). */
export interface ResolvedPmeiNode {
  nodeId: string;
  methodId: string;
}

/**
 * Maps each persisted `ProcedureRunCondition` id to its rendered condition group index (the grid row).
 * A condition id belongs to exactly one group. Shared by the status map and edge resolver.
 */
export function buildConditionGroupIndexByConditionId(
  conditions: Array<ConditionGroup>
): ReadonlyMap<string, number> {
  return new Map<string, number>(
    conditions.flatMap((conditionGroup, index) =>
      Object.values(conditionGroup.conditionIdsByPmdvId).map((conditionId) => [conditionId, index])
    )
  );
}

/**
 * Maps each PMEI id to the grid node that renders it, skipping PMEIs whose condition isn't rendered.
 * Asserts the persisted `replicateNumber` is within the rendered group's `numReplicates`: today the live
 * design can't drop a persisted replicate, so an out-of-range value is a data bug, not an edit.
 */
export function buildPmeiToNodeIdMap(
  infoForPlacements: Array<MethodExecutionInstanceInfoForPlacement>,
  conditions: Array<ConditionGroup>
): ReadonlyMap<string, ResolvedPmeiNode> {
  const conditionIndexById = buildConditionGroupIndexByConditionId(conditions);
  const numReplicatesByConditionId = new Map<string, number>(
    conditions.flatMap((conditionGroup) =>
      Object.values(conditionGroup.conditionIdsByPmdvId).map((conditionId): [string, number] => [
        conditionId,
        conditionGroup.numReplicates,
      ])
    )
  );
  const pmeiToNode = new Map<string, ResolvedPmeiNode>();
  infoForPlacements.forEach(({pmeiId, methodId, conditionId, replicateNumber}) => {
    const conditionIndex = conditionIndexById.get(conditionId);
    if (conditionIndex == null) {
      return;
    }
    const numReplicates = numReplicatesByConditionId.get(conditionId);
    assert(
      numReplicates != null && replicateNumber >= 1 && replicateNumber <= numReplicates,
      `PMEI ${pmeiId} has replicateNumber ${replicateNumber} outside the rendered range [1, ${numReplicates}] for condition ${conditionId}`
    );
    pmeiToNode.set(pmeiId, {
      nodeId: getProcedureRunDesignFlowchartNodeId({
        conditionIndex,
        replicateIndex: replicateNumber - 1,
        methodId,
      }),
      methodId,
    });
  });
  return pmeiToNode;
}

/** Options for `downstream` tinting relative to the active unit-op layer. */
export interface ExecutionEdgeVariantOptions {
  /** Ordered unit-op methods; used to compare the source method against the active one. */
  methods?: Array<MethodIdAndName>;
  /** Active method id, or `null` when there's no active layer. */
  activeMethodId?: string | null;
}

/**
 * Maps a runtime condition graph to {@link FlowchartEdgeSpec}s, skipping edges with an unresolved
 * endpoint. With `activeMethodId`, edges whose source is at or after the active method get
 * `downstream`; others stay `default`.
 */
export function executionEdgeSpecsFromConditionGraph(
  edges: Array<ConditionReplicateEdge>,
  pmeiToNode: ReadonlyMap<string, ResolvedPmeiNode>,
  {methods = [], activeMethodId = null}: ExecutionEdgeVariantOptions = {}
): Array<FlowchartEdgeSpec> {
  const methodIndexById = new Map(methods.map((method, index) => [method.id, index]));
  let activeMethodIndex = -1;
  if (activeMethodId != null) {
    const index = methodIndexById.get(activeMethodId);
    assert(index != null, `Active method ${activeMethodId} is not among the provided methods`);
    activeMethodIndex = index;
  }

  const edgeSpecs: Array<FlowchartEdgeSpec> = [];
  edges.forEach((edge) => {
    const sourceNode = pmeiToNode.get(edge.sourcePmeiId);
    const targetNode = pmeiToNode.get(edge.targetPmeiId);
    if (sourceNode == null || targetNode == null) {
      return;
    }
    edgeSpecs.push({
      sourceNodeId: sourceNode.nodeId,
      targetNodeId: targetNode.nodeId,
      variant: isDownstreamOfActiveLayer(sourceNode.methodId, methodIndexById, activeMethodIndex)
        ? 'downstream'
        : 'default',
    });
  });
  return edgeSpecs;
}

/** True when `sourceMethodId` is at or after the active method in flow order. */
function isDownstreamOfActiveLayer(
  sourceMethodId: string,
  methodIndexById: ReadonlyMap<string, number>,
  activeMethodIndex: number
): boolean {
  if (activeMethodIndex < 0) {
    return false;
  }
  const sourceMethodIndex = methodIndexById.get(sourceMethodId);
  assert(sourceMethodIndex != null, `Source method ${sourceMethodId} is not among the provided methods`);
  return sourceMethodIndex >= activeMethodIndex;
}

/**
 * Returns the largest measured height among the given nodes, falling back to `fallbackHeight`
 * when none of them have been measured yet (height null/0). Used to position grid rows below the
 * column headers using their real rendered height rather than a static estimate.
 */
export function getMaxMeasuredNodeHeight<T>(nodes: Array<Node<T>>, fallbackHeight: number): number {
  const measuredHeights = nodes.map((node) => node.height ?? 0).filter((height) => height > 0);
  return measuredHeights.length > 0 ? Math.max(...measuredHeights) : fallbackHeight;
}

/**
 * Purely geometric grid layout. Takes measured node dimensions from ReactFlow and computes
 * absolute positions for each node. Returns repositioned rows and per-column x/width values
 * for use by recomputeColumnPositions.
 *
 * rows[r][c] must have width/height populated by ReactFlow before calling this.
 */
export function recomputeGridPositions<T>(
  rows: Array<Array<Node<T>>>,
  {horizontalGap, verticalGap, startX, startY}: GridLayoutOptions
): {
  repositionedRows: Array<Array<Node<T>>>;
  columnDimensions: Array<{x: number; width: number}>;
} {
  if (rows.length === 0 || rows[0].length === 0) {
    return {repositionedRows: rows, columnDimensions: []};
  }

  // Each column is as wide as its widest grid cell. Map over the first row to get one width per
  // column (every row has the same number of columns).
  const columnWidths = rows[0].map((_, col) =>
    Math.max(...rows.map((row) => row[col]?.width ?? FALLBACK_NODE_WIDTH))
  );
  const columnDimensions = computeColumnDimensions(columnWidths, {startX, horizontalGap});

  // Reposition rows with cumulative Y, using the tallest node in each row.
  let currentY = startY;
  const repositionedRows = rows.map((row) => {
    const rowHeight = Math.max(
      ...row.map((node) => (node.height != null && node.height > 0 ? node.height : FALLBACK_NODE_HEIGHT))
    );
    const repositionedRow = row.map((node, col) => ({
      ...node,
      position: {x: columnDimensions[col].x, y: currentY},
    }));
    currentY += rowHeight + verticalGap;
    return repositionedRow;
  });

  return {repositionedRows, columnDimensions};
}

/**
 * Lays out the given column widths left-to-right, advancing x by `width + horizontalGap` per column.
 * Single source of truth for column x-spacing, using the widths from the widest grid cell per column.
 */
function computeColumnDimensions(
  widths: Array<number>,
  {startX, horizontalGap}: {startX: number; horizontalGap: number}
): Array<{x: number; width: number}> {
  let currentX = startX;
  return widths.map((width) => {
    const dimension = {x: currentX, width};
    currentX += width + horizontalGap;
    return dimension;
  });
}

/**
 * Positions and sizes column overlay nodes using the column dimensions computed by
 * recomputeGridPositions. Width matches the widest node in each column; height spans the
 * full column from header through the last grid row.
 */
export function recomputeColumnPositions<T>(
  columnNodes: Array<Node<T>>,
  columnDimensions: Array<{x: number; width: number}>,
  {startY, totalHeight}: {startY: number; totalHeight: number}
): Array<Node<T>> {
  assert(
    columnNodes.length === columnDimensions.length,
    'columnNodes and columnDimensions must have the same length'
  );

  return columnNodes.map((node, index) => {
    const dimension = columnDimensions[index];
    return {
      ...node,
      position: {x: dimension.x, y: startY},
      style: {...node.style, width: dimension.width, height: totalHeight},
    };
  });
}

/** Height from column top (startY) through the bottom of the last grid row. */
export function computeColumnTotalHeight(repositionedRows: Array<Array<Node>>, columnStartY: number): number {
  if (repositionedRows.length === 0) {
    return FALLBACK_COLUMN_NODE_HEIGHT;
  }
  const lastRow = repositionedRows[repositionedRows.length - 1];
  if (lastRow.length === 0) {
    return FALLBACK_COLUMN_NODE_HEIGHT;
  }
  const lastRowY = lastRow[0].position.y;
  const lastRowHeight = Math.max(
    ...lastRow.map((node) => (node.height != null && node.height > 0 ? node.height : FALLBACK_NODE_HEIGHT))
  );
  return lastRowY + lastRowHeight - columnStartY;
}
